import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { authenticationService } from 'src/services/authentication.service';
import { get } from 'src/services/httpClient.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Divider,
  Paper
} from '@mui/material';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
import { formatCurrency } from 'src/common/utils/currency';
dayjs.locale('en-gb');

// Define columns for the report download service - UPDATED: Removed userId, added customerId

const RefundPaymentReport = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [modifiedDate, setModifiedDate] = useState<Dayjs | null>(dayjs());

  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  const [subscriberStats, setSubscriberStats] = useState({
    total: 0,
    active: 0,
    suspended: 0,
  });



  const [page, setPage] = useState(0);

  const [rowCount, setRowCount] = useState(0);


  const columns = [

    {
      field: 'customerId',
      headerName: 'Customer ID',
      minWidth: 150,
    }, {
      field: 'refundNo',
      headerName: 'Refund No',
      minWidth: 180,
    },
    {
      field: 'orderId',
      headerName: 'Order ID',
      minWidth: 180,
    },
    {
      field: 'paymentId',
      headerName: 'Payment ID',
      minWidth: 180,
    },
    {
      field: 'vendorId',
      headerName: 'Vendor ID',
      minWidth: 150,
    },
    {
      field: 'refundAmount',
      headerName: 'Refund Amount',
      minWidth: 160,
      type: 'number',
      valueFormatter: (params) =>
        params.value != null ? formatCurrency(params.value) : '',
    }, {
      field: 'details',
      headerName: 'Details',
      minWidth: 100,
      maxWidth: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => {
            setSelectedRow(params.row);
            setDetailsOpen(true);
          }}
          size="small"
        >
          <InfoOutlinedIcon />
        </IconButton>
      ),
    }
  ];


  // Function to fetch broadcasters


  useEffect(() => {

    const fetchData = async () => {


      setLoading(true);
      try {
        const date = modifiedDate
          ? modifiedDate.format("YYYY-MM-DD")
          : "";

        const response = await get(
          `${process.env.REACT_APP_BASE_API_URL}/api/refunds/vendor/${vendorId}/findAll`
        );



        const responseData = response?.data || []


        const dataWithId = responseData.map((item: any, index: number) => ({
          id: item.id ?? index + 1,
          refundNo: item.refundNo,
          orderId: item.orderId,
          paymentId: item.paymentId,
          userId: item.userId,
          customerId: item.customerId,
          vendorId: item.vendorId,
          franchiseId: item.franchiseId,
          lcoId: item.lcoId,
          refundAmount: item.refundAmount,
          refundItems: item.refundItems || [],
          openingBalance: item.openingBalance,
          closingBalance: item.closingBalance,
          refundReason: item.refundReason,
          refundMethod: item.refundMethod,
          requestedBy: item.requestedBy,
          refundStatus: item.refundStatus,
          approvedBy: item.approvedBy,
          remarks: item.remarks,
          status: item.status,
          requestedDate: item.requestedDate,
          approvedDate: item.approvedDate,
          createdDate: item.createdDate,
          updatedDate: item.updatedDate,
        }));

        setRows(dataWithId);
        setRowCount(response?.totalElements ?? dataWithId.length);

        // Calculate stats
        const active = dataWithId.filter((item: any) => item.user_status === 'ACTIVE').length;
        const suspended = dataWithId.filter((item: any) => item.user_status === 'SUSPENDED').length;

        setSubscriberStats({
          total: dataWithId.length,
          active,
          suspended
        });
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    modifiedDate,
    page,
    pageSize]);

  // Generate timestamped filename for Excel only
  const generateTimestampedFilename = (
    baseName: string,

  ): string => {

    const time = dayjs().format('HH-mm-ss');
    return `${baseName}`;
  };

  // Keep your original Excel logic with loading state - UPDATED with customerId
  const handleDownloadExcel = async () => {
    setDownloading((prev) => ({ ...prev, excel: true }));

    try {
      const headers = [
        'ID',
        'Refund No',
        'Order ID',
        'Payment ID',
        'User ID',
        'Customer ID',
        'Vendor ID',
        'Refund Amount',
        'Opening Balance',
        'Closing Balance',
        'Refund Reason',
        'Refund Method',
        'Requested By',
        'Refund Status',
        'Approved By',
        'Remarks',
        'Status',
        'Requested Date',
        'Approved Date',
        'Created Date',
        'Updated Date',
      ];

      const data = filteredRows.map((row: any) => [
        row.id,
        row.refundNo,
        row.orderId,
        row.paymentId,
        row.userId,
        row.customerId,
        row.vendorId,
        row.refundAmount,
        row.openingBalance,
        row.closingBalance,
        row.refundReason,
        row.refundMethod,
        row.requestedBy,
        row.refundStatus,
        row.approvedBy,
        row.remarks,
        row.status,
        row.requestedDate
          ? dayjs(row.requestedDate).format('DD/MM/YYYY HH:mm:ss')
          : '',
        row.approvedDate
          ? dayjs(row.approvedDate).format('DD/MM/YYYY HH:mm:ss')
          : '',
        row.createdDate
          ? dayjs(row.createdDate).format('DD/MM/YYYY HH:mm:ss')
          : '',
        row.updatedDate
          ? dayjs(row.updatedDate).format('DD/MM/YYYY HH:mm:ss')
          : '',
      ]);

      const finalData = [headers, ...data];

      const worksheet = XLSX.utils.aoa_to_sheet(finalData);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        'Refund Payment Report'
      );

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, 'Refund Payment Report.xlsx');
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading((prev) => ({ ...prev, excel: false }));
    }
  };

  // Single download handler for PDF using common service - UPDATED with customerId
  const handleDownloadPDF = async () => {
    setDownloading((prev) => ({ ...prev, pdf: true }));

    try {
      const pdfColumns = [
        { field: 'id', headerName: 'ID' },
        { field: 'refundNo', headerName: 'Refund No' },
        { field: 'orderId', headerName: 'Order ID' },
        { field: 'paymentId', headerName: 'Payment ID' },
        { field: 'userId', headerName: 'User ID' },
        { field: 'customerId', headerName: 'Customer ID' },
        { field: 'vendorId', headerName: 'Vendor ID' },
        { field: 'refundAmount', headerName: 'Refund Amount' },
        { field: 'openingBalance', headerName: 'Opening Balance' },
        { field: 'closingBalance', headerName: 'Closing Balance' },
        { field: 'refundReason', headerName: 'Refund Reason' },
        { field: 'refundMethod', headerName: 'Refund Method' },
        { field: 'requestedBy', headerName: 'Requested By' },
        { field: 'refundStatus', headerName: 'Refund Status' },
        { field: 'approvedBy', headerName: 'Approved By' },
        { field: 'remarks', headerName: 'Remarks' },
        { field: 'status', headerName: 'Status' },
        { field: 'requestedDate', headerName: 'Requested Date' },
        { field: 'approvedDate', headerName: 'Approved Date' },
        { field: 'createdDate', headerName: 'Created Date' },
        { field: 'updatedDate', headerName: 'Updated Date' },
      ];

      const pdfRows = filteredRows.map((row: any) => ({
        ...row,
        requestedDate: row.requestedDate
          ? dayjs(row.requestedDate).format('DD/MM/YYYY HH:mm:ss')
          : '',
        approvedDate: row.approvedDate
          ? dayjs(row.approvedDate).format('DD/MM/YYYY HH:mm:ss')
          : '',
        createdDate: row.createdDate
          ? dayjs(row.createdDate).format('DD/MM/YYYY HH:mm:ss')
          : '',
        updatedDate: row.updatedDate
          ? dayjs(row.updatedDate).format('DD/MM/YYYY HH:mm:ss')
          : '',
      }));

      const success = await reportDownloadService.downloadReport(
        {
          title: 'Refund Payment Report',
          columns: pdfColumns,
          rows: pdfRows,
          fileName: 'Refund Payment Report.pdf',
        },
        'pdf'
      );

      if (!success) {
        alert('Failed to generate PDF. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setDownloading((prev) => ({ ...prev, pdf: false }));
    }
  };

  const filteredRows = rows.filter((row: any) => {
    // Search filter
    const matchesSearch = Object.values(row).some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Created Date filter
    const matchesDate = modifiedDate
      ? dayjs(row.createdDate).isSame(modifiedDate, 'day')
      : true;

    return matchesSearch && matchesDate;
  });

  return (
    <>


      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle
              heading="Refund Payment Report"
              subHeading="View Subscribers Refund Payment Report"
              handleClick={undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasPermission(["OWNER"]) && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadExcel}
                  disabled={downloading.excel}
                >
                  {downloading.excel ? 'Generating...' : 'Download Excel'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={downloading.pdf}
                >
                  {downloading.pdf ? 'Generating...' : 'Download PDF'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </PageTitleWrapper>

      {/* STATS SECTION - Commented out */}
      {/* <Box sx={{ px: 2, pt: 0, pb: 2 }}>
        <Paper elevation={4} sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'space-between' }}>
          <Typography variant="subtitle1">Total Subscribers: <strong>{subscriberStats.total}</strong></Typography>
          <Typography variant="subtitle1">Active Subscribers: <strong>{subscriberStats.active}</strong></Typography>
          <Typography variant="subtitle1">Suspended Subscribers: <strong>{subscriberStats.suspended}</strong></Typography>
        </Paper>
      </Box> */}

      {/* FILTER SECTION */}
      <Box sx={{ px: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
          >
            <TextField
              label="Search"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 220 }}
            />


            {/* <TextField
      label="Broadcaster ID"
      size="small"
      value={broadcasterId}
      onChange={(e) => setBroadcasterId(e.target.value)}
      sx={{ minWidth: 220 }}
    /> */}

            <DatePicker
              label="Date"
              value={modifiedDate}
              onChange={(newValue) => setModifiedDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 180 }
                }
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Box>

      {/* DATAGRID SECTION */}
      <Box sx={{ height: 'calc(100vh - 280px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          rowCount={rowCount}
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </Box>

      {/* dialog box */}
      <Dialog
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedRow(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Refund Details
        </DialogTitle>

        <DialogContent dividers>
          {selectedRow && (
            <Grid container spacing={2}>

              {/* Basic Details */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 1 }}
                >
                  Basic Details
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Refund No
                </Typography>
                <Typography variant="body1">
                  {selectedRow.refundNo || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Customer ID
                </Typography>
                <Typography variant="body1">
                  {selectedRow.customerId || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Order ID
                </Typography>
                <Typography variant="body1">
                  {selectedRow.orderId || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Payment ID
                </Typography>
                <Typography variant="body1">
                  {selectedRow.paymentId || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  User ID
                </Typography>
                <Typography variant="body1">
                  {selectedRow.userId || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Vendor ID
                </Typography>
                <Typography variant="body1">
                  {selectedRow.vendorId || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Franchise ID
                </Typography>
                <Typography variant="body1">
                  {selectedRow.franchiseId || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  LCO ID
                </Typography>
                <Typography variant="body1">
                  {selectedRow.lcoId || '-'}
                </Typography>
              </Grid>

              {/* Refund Details */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 1 }}
                >
                  Refund Information
                </Typography>
                <Divider />
              </Grid>

              {/* Refund Items */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 1 }}
                >
                  Refund Items
                </Typography>

                <Divider />
              </Grid>

              <Grid item xs={12}>
                {selectedRow.refundItems &&
                  selectedRow.refundItems.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxHeight: 300 }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Item Type</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Item ID</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Item Name</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Refund Amount</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Status</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Created Date</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {selectedRow.refundItems.map(
                          (item: any, index: number) => (
                            <TableRow key={item.id || index}>
                              <TableCell>
                                {item.itemType || '-'}
                              </TableCell>

                              <TableCell>
                                {item.itemId ?? '-'}
                              </TableCell>

                              <TableCell>
                                {item.itemName || '-'}
                              </TableCell>

                              <TableCell>
                                {item.refundAmount != null
                                  ? formatCurrency(item.refundAmount)
                                  : '-'}
                              </TableCell>

                              <TableCell>
                                {item.status || '-'}
                              </TableCell>

                              <TableCell>
                                {item.createdDate
                                  ? dayjs(item.createdDate).format(
                                    'DD/MM/YYYY HH:mm:ss'
                                  )
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    No refund items found.
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Refund Amount
                </Typography>
                <Typography variant="body1">
                  {selectedRow.refundAmount != null
                    ? formatCurrency(selectedRow.refundAmount)
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Opening Balance
                </Typography>
                <Typography variant="body1">
                  {selectedRow.openingBalance != null
                    ? formatCurrency(selectedRow.openingBalance)
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Closing Balance
                </Typography>
                <Typography variant="body1">
                  {selectedRow.closingBalance != null
                    ? formatCurrency(selectedRow.closingBalance)
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Refund Method
                </Typography>
                <Typography variant="body1">
                  {selectedRow.refundMethod || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Refund Status
                </Typography>
                <Typography variant="body1">
                  {selectedRow.refundStatus || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Typography variant="body1">
                  {selectedRow.status || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Refund Reason
                </Typography>
                <Typography variant="body1">
                  {selectedRow.refundReason || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Remarks
                </Typography>
                <Typography variant="body1">
                  {selectedRow.remarks || '-'}
                </Typography>
              </Grid>

              {/* Approval Details */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 1 }}
                >
                  Approval Details
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Requested By
                </Typography>
                <Typography variant="body1">
                  {selectedRow.requestedBy || '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Approved By
                </Typography>
                <Typography variant="body1">
                  {selectedRow.approvedBy || '-'}
                </Typography>
              </Grid>

              {/* Dates */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 1 }}
                >
                  Date Information
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Requested Date
                </Typography>
                <Typography variant="body1">
                  {selectedRow.requestedDate
                    ? dayjs(selectedRow.requestedDate).format(
                      'DD/MM/YYYY HH:mm:ss'
                    )
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Approved Date
                </Typography>
                <Typography variant="body1">
                  {selectedRow.approvedDate
                    ? dayjs(selectedRow.approvedDate).format(
                      'DD/MM/YYYY HH:mm:ss'
                    )
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Created Date
                </Typography>
                <Typography variant="body1">
                  {selectedRow.createdDate
                    ? dayjs(selectedRow.createdDate).format(
                      'DD/MM/YYYY HH:mm:ss'
                    )
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Updated Date
                </Typography>
                <Typography variant="body1">
                  {selectedRow.updatedDate
                    ? dayjs(selectedRow.updatedDate).format(
                      'DD/MM/YYYY HH:mm:ss'
                    )
                    : '-'}
                </Typography>
              </Grid>

            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setDetailsOpen(false);
              setSelectedRow(null);
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RefundPaymentReport;

